import ky from 'ky';
import { apiRequest, graphRequest } from '../../config/authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-common';
import { API_URL } from '../../config/consts';
import { msalInstance } from '../../auth/msalInstance';
import { SilentRequest } from '@azure/msal-browser';
import { HttpHeader } from '../../Model/enums/HttpHeader';
import { MimeValueType } from '../../Model/enums/MimeType';

const getTokenResponse = async (isMsGraphEndpoint: boolean) => {
  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();
  if (!activeAccount && accounts.length === 0) {
    throw new Error('User is not signed in.');
  }
  const silentRequest: SilentRequest = {
    account: activeAccount || accounts[0],
    scopes: []
  };

  if (isMsGraphEndpoint) {
    silentRequest.scopes = graphRequest.scopes;
  } else {
    silentRequest.scopes = apiRequest.scopes;
  }

  return msalInstance.acquireTokenSilent(silentRequest).catch(async error => {
    if (error instanceof InteractionRequiredAuthError) {
      // fallback to interaction when silent call fails
      return msalInstance.acquireTokenRedirect(silentRequest);
    }
    throw error;
  });
};

const setDefaultHeaders = (options: Request) => {
  const headersKeys = Array.from(options.headers.keys()).map(key => key.toLowerCase());
  if (!headersKeys.includes(HttpHeader.ContentType)) {
    options.headers.set(HttpHeader.ContentType, MimeValueType.Json);
  }
  if (!headersKeys.includes(HttpHeader.Accept)) {
    options.headers.set(HttpHeader.Accept, MimeValueType.Json);
  }
};

const addAuthToken = async (request: Request, isMsGraphEndpoint: boolean) => {
  const tokenResponse = await getTokenResponse(isMsGraphEndpoint);
  if (tokenResponse) {
    request.headers.set(HttpHeader.Authorization, `Bearer ${tokenResponse.accessToken}`);
  }
};

const handleUnauthorizedResponse = async (request: Request, response: Response, isMsGraphEndpoint: boolean) => {
  if (response.status !== 401) {
    return response;
  }

  const tokenResponse = await getTokenResponse(isMsGraphEndpoint);
  if (!tokenResponse) {
    return response;
  }

  const token = isMsGraphEndpoint ? tokenResponse.accessToken : tokenResponse.idToken;
  request.headers.set(HttpHeader.Authorization, `Bearer ${token}`);
  return ky(request);
};

const createKyClient = (isMsGraphEndpoint: boolean, timeout?: number) => {
  return ky.extend({
    timeout: timeout ?? 100000,
    retry: 3,
    hooks: {
      beforeRequest: [
        options => {
          if (options.method !== 'GET') {
            setDefaultHeaders(options);
          }
        },
        async request => addAuthToken(request, isMsGraphEndpoint)
      ],
      afterResponse: [
        async (request, options, response) => handleUnauthorizedResponse(request, response, isMsGraphEndpoint)
      ]
    },
    prefixUrl: isMsGraphEndpoint ? undefined : API_URL
  });
};

export default createKyClient;
